"use client"

import { useEffect, useRef, useState, type RefObject } from "react"

export interface SeenMark {
  conversationId: number
  /** Highest Chatwoot message id accounted for when the mark was reported. */
  messageId: number
}

/**
 * Decide whether a fresh "seen" mark should be reported. Returns the mark to
 * send, or `null` when there's nothing new to report. Pure (no DOM) so the
 * gating logic can be unit-tested directly.
 *
 * `active` folds together the visibility gates (panel open + tab focused +
 * latest messages on screen). We only advance the marker when it's active AND
 * something newer than the last report exists — so scrolling or refocusing with
 * no new message never re-fires, while each newly-arrived inbound message does.
 */
export function nextSeenMark(
  active: boolean,
  conversationId: number | null,
  latestMessageId: number | null,
  last: SeenMark | null
): SeenMark | null {
  if (!active) return null
  if (!conversationId) return null
  // A conversation with no Chatwoot message yet still marks read once (the
  // visitor is looking at it); 0 is below any real id so it never masks one.
  const messageId = latestMessageId ?? 0
  if (
    last &&
    last.conversationId === conversationId &&
    last.messageId >= messageId
  ) {
    return null
  }
  return { conversationId, messageId }
}

interface UseReadReceiptsArgs {
  /** Active conversation id, from use-chat-session. */
  conversationId: number | null
  /** The per-contact widget JWT (session.source_id), sent as X-Auth-Token. */
  authToken: string | null
  /** Monotonic high-water mark of Chatwoot messages seen
   *  (session.last_chatwoot_message_id) — advances on each inbound message. */
  latestMessageId: number | null
}

/**
 * Reports read state to Chatwoot (`contact_last_seen_at`, via `/api/chat/seen`)
 * so the agent dashboard's "Seen" indicator reflects that the visitor has read
 * the latest messages — but only while the chat is actually being looked at.
 *
 * Visibility-gated three ways:
 *  - the panel is open (this hook only runs while ChatPanel is mounted),
 *  - the tab/window is focused (`document.visibilityState === "visible"`),
 *  - the newest messages are on screen (an IntersectionObserver on the bottom
 *    sentinel — attach the returned `anchorRef` to a node after the last
 *    message).
 *
 * It fires once when those conditions first hold with an unmarked conversation,
 * and again on each newly-rendered inbound message while they still hold. It
 * never fires when the panel is backgrounded or scrolled up in history.
 */
export function useReadReceipts({
  conversationId,
  authToken,
  latestMessageId,
}: UseReadReceiptsArgs): { anchorRef: RefObject<HTMLDivElement | null> } {
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [tabVisible, setTabVisible] = useState(true)
  const [latestOnScreen, setLatestOnScreen] = useState(false)
  const lastReported = useRef<SeenMark | null>(null)

  // Tab focus.
  useEffect(() => {
    if (typeof document === "undefined") return
    const update = (): void =>
      setTabVisible(document.visibilityState === "visible")
    update()
    document.addEventListener("visibilitychange", update)
    return () => document.removeEventListener("visibilitychange", update)
  }, [])

  // Are the newest messages on screen? The sentinel sits below the last
  // message; it's clipped out of the scroll viewport whenever the visitor
  // scrolls up into history, so intersecting == "reading the latest".
  useEffect(() => {
    const el = anchorRef.current
    if (!el || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      (entries) => setLatestOnScreen(entries.some((e) => e.isIntersecting)),
      { threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Report a mark when the gates hold and something new is unread.
  useEffect(() => {
    if (!authToken) return
    const active = tabVisible && latestOnScreen
    const mark = nextSeenMark(
      active,
      conversationId,
      latestMessageId,
      lastReported.current
    )
    if (!mark) return
    lastReported.current = mark
    void fetch("/api/chat/seen", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Auth-Token": authToken,
      },
      body: JSON.stringify({ conversation_id: mark.conversationId }),
    }).catch(() => {
      // Best-effort: a dropped mark just isn't reflected in the dashboard yet.
      // The next inbound message (or a refocus) re-triggers it.
    })
  }, [tabVisible, latestOnScreen, conversationId, latestMessageId, authToken])

  return { anchorRef }
}
